/**
 * FileManagement Page
 * Main file management admin page
 */

import React, { useState } from 'react';
import { Container, Group } from '@/components/ui/layout';
import { Card } from '@/components/ui/data-display';
import { Text, Title } from '@/components/ui/core';
import { Tabs } from '@/components/ui/navigation';
import { IconFiles, IconUpload, IconChartBar } from '@tabler/icons-react';
import { FileBrowserTab, UploadTab, StorageStatsTab, FileDetailsModal, FilePreviewModal } from './components';
import { FileRecordModelView } from './types';

export const FileManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('browser');
  const [selectedFile, setSelectedFile] = useState<FileRecordModelView | null>(null);
  const [detailsModalOpened, setDetailsModalOpened] = useState(false);
  const [previewModalOpened, setPreviewModalOpened] = useState(false);

  const handleFileDetails = (file: FileRecordModelView) => {
    setSelectedFile(file);
    setDetailsModalOpened(true);
  };

  const handleFilePreview = (file: FileRecordModelView) => {
    setSelectedFile(file);
    setPreviewModalOpened(true);
  };

  const handleUploadComplete = () => {
    setActiveTab('browser');
  };

  return (
    <Container size="xl" py="xl">
      <Card shadow="sm" p="xl" radius="md" variant="elevated">
        {/* Header */}
        <Group justify="space-between" mb="xl">
          <div>
            <Title order={2}>File Management</Title>
            <Text c="dimmed" size="sm">
              Manage files and folders with AWS S3 storage
            </Text>
          </div>
        </Group>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'browser')}>
          <Tabs.List>
            <Tabs.Tab value="browser" leftSection={<IconFiles size={16} />}>
              File Browser
            </Tabs.Tab>
            <Tabs.Tab value="upload" leftSection={<IconUpload size={16} />}>
              Upload
            </Tabs.Tab>
            <Tabs.Tab value="statistics" leftSection={<IconChartBar size={16} />}>
              Statistics
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="browser" pt="md">
            <FileBrowserTab
              onFileDetails={handleFileDetails}
              onFilePreview={handleFilePreview}
            />
          </Tabs.Panel>

          <Tabs.Panel value="upload" pt="md">
            <UploadTab onUploadComplete={handleUploadComplete} />
          </Tabs.Panel>

          <Tabs.Panel value="statistics" pt="md">
            <StorageStatsTab />
          </Tabs.Panel>
        </Tabs>
      </Card>

      {/* Modals */}
      <FileDetailsModal
        file={selectedFile}
        opened={detailsModalOpened}
        onClose={() => setDetailsModalOpened(false)}
        onDownload={async (fileId, fileName) => {
          const { useS3Operations } = await import('./hooks/useS3Operations');
          const s3Ops = useS3Operations();
          await s3Ops.downloadFile(fileId, fileName);
        }}
      />

      <FilePreviewModal
        file={selectedFile}
        opened={previewModalOpened}
        onClose={() => setPreviewModalOpened(false)}
      />
    </Container>
  );
};

export default FileManagement;
